import { createApi, skipToken } from '@reduxjs/toolkit/query/react';
import {
    SearchSortOrderApiDto,
    type IndependentSearchApiRequest,
    type IndependentSearchApiResponse,
} from './types/webapi-discover-generated';
import { isServer } from 'tradera/utils/nextjs';
import { isomorphicBaseQueryForWishListableItems } from './utils/webapi-base-query';
import { createLogger } from 'tradera/utils/logging/isomorphic-logger';
import { DEFAULT_LANGUAGE, URL_LANGUAGES } from 'tradera/lang/constants.mjs';

const log = createLogger('state/services/independent-search-api');

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getLastSearchQueryParams = (
    queryParams: Record<string, unknown> | undefined,
) => {
    const modifiedParams = {
        ...queryParams,
        // We always want to sort on newest added to get a more "alive" feeling on the start page.
        // And it would be confusing for the user if they clicked the link to get to all search
        // results if they didn't see the same items on top as they did on the start page.
        sortBy: SearchSortOrderApiDto.AddedOn,
    };
    return modifiedParams;
};

export const independentSearchApi = createApi({
    reducerPath: 'independentSearchApi',
    baseQuery: isomorphicBaseQueryForWishListableItems,
    endpoints: (builder) => ({
        getIndependentSearchResult: builder.query<
            IndependentSearchApiResponse,
            IndependentSearchApiRequest
        >({
            query: (parameters) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                return {
                    url: `${baseUrl}/discover/web/independent-search`,
                    method: 'POST',
                    body: parameters,
                };
            },
            extraOptions: {
                getWishListableItems: (
                    response: IndependentSearchApiResponse,
                ) => response.items,
            },
        }),
    }),
});

export const isValidIndependentSearchParameters = (
    parameters: IndependentSearchApiRequest,
) => {
    const hasSellerLocation =
        !!parameters.sellerLocationLat && !!parameters.sellerLocationLong;

    if (!parameters.query && !hasSellerLocation) {
        return false;
    }

    if (parameters.shippingCountryCodeIso2?.length !== 2) {
        return false;
    }

    if (
        parameters.languageCodeIso2 !== DEFAULT_LANGUAGE &&
        !URL_LANGUAGES.includes(parameters.languageCodeIso2 || '')
    ) {
        return false;
    }

    return true;
};

export const useGetIndependentSearchResultQuery = (
    ...args: Parameters<
        typeof independentSearchApi.useGetIndependentSearchResultQuery
    >
) => {
    const params = args[0];
    const options = args[1];
    const skip = params === skipToken || options?.skip === true;

    if (!skip && !isValidIndependentSearchParameters(params)) {
        log.error('useGetIndependentSearchResultQuery has invalid parameters', {
            ...params,
            attributes: Object.keys(params.attributes ?? {}),
        });
    }
    return independentSearchApi.useGetIndependentSearchResultQuery(...args);
};

export const { endpoints } = independentSearchApi;
